import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '@/components/core';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui-kit/empty';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { type TableDensity, useTableDensity } from '@/hooks/use-table-density';
import { cn } from '@/lib/utils';
import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import { PersonAvatar } from '@/components/core/person-avatar/person-avatar';
import type { Presence } from '@/components/core/presence-avatar/presence-avatar';
import { addDays, daySpanOf, formatDayLabel } from '../../utils/leave-calendar-week';

interface LeaveCalendarAgendaProps {
  /** The days of the month shown ("YYYY-MM-DD"). */
  days: string[];
  today: string;
  locale: string;
  employees: LeaveCalendarEmployeeGroup[];
  isLoading: boolean;
  /** "No leave in October 2026". */
  emptyMessage: string;
  /** What shows under the name by IAM user id: designation and department, else the email. */
  subtitleOf?: (userId: string) => string | undefined;
  /** A day that is the person's weekend; leave days on it are not counted. */
  weekendOf?: (userId: string, day: string) => boolean;
  /** Today's presence per person, for the avatars (as People shows it). */
  presenceOf?: (userId: string) => { presence?: Presence; presenceLabel?: string };
  onOpenLeave: (leave: LeaveCalendarLeaveEvent, employee: LeaveCalendarEmployeeGroup) => void;
}

const LEAVE_TYPE_KEYS: Record<string, string> = { FullDay: 'FULL_DAY', HalfDay: 'HALF_DAY' };

/** An item's vertical padding by row density (condensed is the prototype's 10px). */
const DENSITY_ROW_PADDING: Record<TableDensity, string> = {
  condensed: 'py-2.5',
  regular: 'py-3.5',
  relaxed: 'py-[18px]',
};

/** The side bar of an item by status (prototype `.ag-bar.lb-*`). */
const STATUS_BAR: Record<string, string> = {
  Approved: 'bg-primary',
  Pending: 'bg-warning',
  Rejected: 'bg-destructive',
};

interface AgendaEntry {
  leave: LeaveCalendarLeaveEvent;
  employee: LeaveCalendarEmployeeGroup;
  /** The day the leave shows under: its first day, or the month's first when it began earlier. */
  day: string;
  startedEarlier: boolean;
}

/**
 * Every leave of the month grouped by the day it starts (prototype Leaves view): a date column, then
 * one row per leave with who, what, when and the status.
 */
export const LeaveCalendarAgenda = ({
  days,
  today,
  locale,
  employees,
  isLoading,
  emptyMessage,
  subtitleOf,
  weekendOf,
  presenceOf,
  onOpenLeave,
}: LeaveCalendarAgendaProps) => {
  const { t } = useTranslation();
  const [density] = useTableDensity();
  const first = days[0];

  const entries: AgendaEntry[] = [];
  for (const employee of employees) {
    for (const leave of employee.LeaveEvents) {
      if (!daySpanOf(leave.StartDate, leave.EndDate, days)) continue;
      const start = dateOnlyOf(leave.StartDate);
      entries.push({
        leave,
        employee,
        day: start < first ? first : start,
        startedEarlier: start < first,
      });
    }
  }
  entries.sort(
    (a, b) =>
      a.day.localeCompare(b.day) || a.employee.DisplayName.localeCompare(b.employee.DisplayName)
  );
  const groups = new Map<string, AgendaEntry[]>();
  for (const entry of entries) groups.set(entry.day, [...(groups.get(entry.day) ?? []), entry]);

  /** Working days of the leave: its days that are not the person's weekend, half a day for a half day. */
  const workingDaysOf = ({ leave, employee }: AgendaEntry) => {
    const start = dateOnlyOf(leave.StartDate);
    const end = dateOnlyOf(leave.EndDate) || start;
    let count = 0;
    for (let day = start; day <= end; day = addDays(day, 1))
      if (!weekendOf?.(employee.ItemId, day)) count += 1;
    return leave.LeaveType === 'HalfDay' ? count * 0.5 : count;
  };
  const short = (day: string) => formatDayLabel(day, locale, { day: 'numeric', month: 'short' });

  if (isLoading)
    return (
      <div className="grid gap-px p-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    );

  if (groups.size === 0)
    return (
      <Empty className="py-10">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Calendar />
          </EmptyMedia>
          <EmptyTitle>{emptyMessage}</EmptyTitle>
          <EmptyDescription>{t('LEAVE_CALENDAR_EMPTY_HINT')}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );

  return (
    <div className="grid overflow-x-auto">
      {[...groups.entries()].map(([day, items]) => {
        const isToday = day === today;
        return (
          <section
            key={day}
            className="grid grid-cols-[72px_1fr] border-b last:border-b-0"
            aria-label={formatDayLabel(day, locale, { dateStyle: 'full' })}
          >
            <div className="grid content-start justify-items-center border-r py-3.5 text-xs text-muted-foreground">
              <span className={cn(isToday && 'font-semibold text-primary')}>
                {formatDayLabel(day, locale, { weekday: 'short' })}
              </span>
              <b
                className={cn(
                  'text-[22px] font-semibold leading-7 tabular-nums text-foreground',
                  isToday && 'text-primary'
                )}
              >
                {Number(day.slice(8))}
              </b>
              <span>{formatDayLabel(day, locale, { month: 'short' })}</span>
            </div>
            <div className="grid">
              {items.map((entry) => {
                const { leave, employee, startedEarlier } = entry;
                const typeKey = leave.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;
                const start = dateOnlyOf(leave.StartDate);
                const end = dateOnlyOf(leave.EndDate) || start;
                const email = subtitleOf?.(employee.ItemId) ?? employee.Email;
                return (
                  <button
                    key={leave.ItemId}
                    type="button"
                    onClick={() => onOpenLeave(leave, employee)}
                    className={cn(
                      'grid min-w-[760px] grid-cols-[4px_32px_minmax(160px,1.4fr)_minmax(140px,1fr)_minmax(120px,.8fr)_auto] items-center gap-3 border-b pl-3 pr-4 text-left outline-none last:border-b-0 hover:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                      // Follows the row density every table shares.
                      DENSITY_ROW_PADDING[density]
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'h-8 w-1 rounded-sm',
                        STATUS_BAR[leave.Status] ?? STATUS_BAR.Approved
                      )}
                    />
                    <PersonAvatar name={employee.DisplayName} {...presenceOf?.(employee.ItemId)} />
                    <span className="grid min-w-0">
                      <b className="truncate font-medium">{employee.DisplayName}</b>
                      {email && (
                        <span className="truncate text-xs text-muted-foreground">{email}</span>
                      )}
                    </span>
                    <span className="grid min-w-0">
                      <b className="truncate font-medium">{leave.LeaveCategory || leave.Title}</b>
                      <span className="truncate text-xs text-muted-foreground">
                        {typeKey ? t(typeKey) : ''}
                        {startedEarlier && ` · ${t('LEAVE_CALENDAR_STARTED_EARLIER')}`}
                      </span>
                    </span>
                    <span className="grid min-w-0 tabular-nums">
                      <b className="truncate font-medium">
                        {start === end ? short(start) : `${short(start)} – ${short(end)}`}
                      </b>
                      <span className="text-xs text-muted-foreground">
                        {t('LEAVE_CALENDAR_DAY_COUNT', { count: workingDaysOf(entry) })}
                      </span>
                    </span>
                    <StatusChip status={leave.Status}>{t(leave.Status.toUpperCase())}</StatusChip>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

/** How many leaves the agenda lists for these employees and days (the footer's count). */
export const agendaLeaveCount = (employees: LeaveCalendarEmployeeGroup[], days: string[]) =>
  employees.reduce(
    (total, employee) =>
      total +
      employee.LeaveEvents.filter((leave) => daySpanOf(leave.StartDate, leave.EndDate, days))
        .length,
    0
  );
